import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Installation } from "data/api";
import { containsQuery, formatDateTime } from "src/util";
import { LoadingPage } from "src/views/LoadingPage/LoadingPage";

import SearchableTable from "components/SearchableTable";
import { TableComponent } from "components/TableComponent";
import { Monospace } from "components/Typography/Monospace";

import { useListAllInstallations } from "./useListAllInstallations";

type GroupedInstallations = {
  groupRef: string;
  groupName: string;
  installations: Installation[];
  // Precomputed once per group so the sort comparator and row don't recompute it.
  lastUpdated: Date | null;
};

const customersColumns = [
  { name: '', style: { width: '4rem' } }, // Chevron column
  {
    name: "Customer Name",
    sortFunction: (a: GroupedInstallations, b: GroupedInstallations) =>
      a.groupName.localeCompare(b.groupName),
    style: { width: '30%', minWidth: '150px' }
  },
  {
    name: "Group Ref",
    sortFunction: (a: GroupedInstallations, b: GroupedInstallations) =>
      a.groupRef.localeCompare(b.groupRef),
    style: { width: '25%', minWidth: '120px', maxWidth: '250px' }
  },
  {
    name: "Total Installations",
    sortFunction: (a: GroupedInstallations, b: GroupedInstallations) =>
      a.installations.length - b.installations.length,
    style: { width: '20%', minWidth: '120px' }
  },
  {
    name: "Last Updated",
    sortFunction: (a: GroupedInstallations, b: GroupedInstallations) => {
      const aTime = a.lastUpdated;
      const bTime = b.lastUpdated;

      if (!aTime && !bTime) return 0;
      if (!aTime) return 1;
      if (!bTime) return -1;

      return aTime.getTime() - bTime.getTime();
    },
    style: { width: '25%', minWidth: '120px', whiteSpace: 'nowrap' }
  },
];

const customersSearchFunction = (
  query: string,
  group: GroupedInstallations
) => {
  if (!group) return false;

  const { groupRef, groupName } = group;
  const integrationNames = group.installations
    .map((i) => i.integrationId)
    .join(" ");

  return containsQuery(query, groupRef, groupName, integrationNames);
};

/**
 * Gets the most recent update time from an array of installations.
 * Falls back to createTime if updateTime is not available.
 * @param installations Array of Installation objects
 * @returns Date object representing the most recent time, or null if no valid times found
 */
function getLastUpdatedTimeFromInstallations(installations: Installation[]): Date | null {
  const times = installations
    .map((installation) => installation.updateTime || installation.createTime)
    .filter(Boolean);
  
  if (times.length === 0) return null;
  
  const mostRecent = new Date(Math.max(...times.map((time) => time.getTime())));
  return mostRecent;
}

const CustomerRow = ({ group }: { group: GroupedInstallations }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // Precomputed when the groups were built (see groupedInstallations below).
  const { lastUpdated } = group;

  const handleRowClick = () => {
    navigate(`/projects/${projectId}/customers/${group.groupRef}`);
  };

  return (
    <TableComponent.TableRowClickable
      onRowClick={handleRowClick}
      ariaLabel={`View customer ${group.groupName} details`}
    >
      <TableComponent.TableCell>{group.groupName}</TableComponent.TableCell>
      <TableComponent.TableCell
        style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        <Monospace>{group.groupRef}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{group.installations.length}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell style={{ whiteSpace: 'nowrap' }}>
        <Monospace>{formatDateTime(lastUpdated)}</Monospace>
      </TableComponent.TableCell>
    </TableComponent.TableRowClickable>
  );
};

export const CustomersTable = () => {
  const { data: installations, isLoading, isError } = useListAllInstallations();

  const groupedInstallations = useMemo(() => {
    if (!installations) return [];

    const groups = installations.reduce(
      (acc: Record<string, GroupedInstallations>, installation) => {
        const groupRef = installation.group?.groupRef;
        if (!groupRef) return acc;

        if (!acc[groupRef]) {
          acc[groupRef] = {
            groupRef,
            groupName: installation.group?.groupName || groupRef,
            installations: [],
            lastUpdated: null,
          };
        }

        acc[groupRef].installations.push(installation);
        return acc;
      },
      {}
    );

    // Compute each group's last-updated time once, here, rather than repeatedly
    // inside the sort comparator and the row component.
    return Object.values(groups).map((group) => ({
      ...group,
      lastUpdated: getLastUpdatedTimeFromInstallations(group.installations),
    }));
  }, [installations]);

  if (isError) {
    return <p className="p-4">There was an error loading the customers.</p>;
  }

  if (isLoading) return <LoadingPage variant="table" />;

  if (!groupedInstallations.length) {
    return null;
  }

  return (
    <SearchableTable
      initialData={groupedInstallations}
      searchFunction={customersSearchFunction}
      rowComponent={(group: GroupedInstallations) => (
        <CustomerRow key={group.groupRef} group={group} />
      )}
      columns={customersColumns}
      initialSortState={{ columnName: "Last Updated", direction: "desc" }}
    />
  );
};
